import React, { useEffect, useState } from "react";
import AiDiagnosisService from "../../services/AiDiagnosisService";
import { isSet } from "../../utils/commonUtils";
import Alert from "../../components/UI/ConfirmBox/Alert";
import withReduxState from "../../hoc/wReduxState";
import AIPredictiveCharting from "./AIPredictiveCharting";

const AIPrediction = ({ incidentDetails, Toast, visitId, organizationId, onSuggestionAccepted, aiSuggestionsRefreshTrigger, onEmrSave, profile }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showConfirmationAlert, setShowConfirmationAlert] = useState(false);
  const [pendingSuggestion, setPendingSuggestion] = useState(null);
  const [lastKnownSuggestionTimestamp, setLastKnownSuggestionTimestamp] = useState(null);
  const [newSuggestionsAvailable, setNewSuggestionsAvailable] = useState(false);
  const [isPollingForNewSuggestions, setIsPollingForNewSuggestions] = useState(false);
  const [hasCompletedPolling, setHasCompletedPolling] = useState(false);
  
  // Check if AI suggestions are enabled for the user
  const aiSuggestionsEnabled = profile?.ai_suggestions_enabled ?? false;

  // Fetch stored suggestions from database
  const fetchStoredSuggestions = async (checkForNew = false) => {
    if (!visitId) return;
    
    try {
      setLoading(true);
      setError("");
      const data = await AiDiagnosisService.getSuggestions(visitId);
      const newSuggestions = data?.suggestions || [];
      
      // Check if we have new suggestions (by comparing timestamps)
      if (checkForNew) {
        if (newSuggestions.length > 0) {
          // We have suggestions - check if they're new
          if (lastKnownSuggestionTimestamp && lastKnownSuggestionTimestamp !== "CHECKED_EMPTY") {
            // Compare timestamps to see if we have new suggestions
            const latestSuggestion = newSuggestions.reduce((latest, current) => {
              const currentTime = new Date(current.created_at || 0).getTime();
              const latestTime = new Date(latest.created_at || 0).getTime();
              return currentTime > latestTime ? current : latest;
            }, newSuggestions[0]);
            
            const latestTime = new Date(latestSuggestion.created_at || 0).getTime();
            const lastKnownTime = new Date(lastKnownSuggestionTimestamp).getTime();
            
            if (latestTime > lastKnownTime) {
              // New suggestions detected - stop polling immediately
              setNewSuggestionsAvailable(true);
              setIsPollingForNewSuggestions(false);
              setLastKnownSuggestionTimestamp(latestSuggestion.created_at);
              setHasCompletedPolling(true);
              // Continue to set suggestions below - don't return early
            }
          } else {
            // No previous timestamp but we now have suggestions - these are new
            const latestSuggestion = newSuggestions.reduce((latest, current) => {
              const currentTime = new Date(current.created_at || 0).getTime();
              const latestTime = new Date(latest.created_at || 0).getTime();
              return currentTime > latestTime ? current : latest;
            }, newSuggestions[0]);
            
            setNewSuggestionsAvailable(true);
            setIsPollingForNewSuggestions(false);
            setLastKnownSuggestionTimestamp(latestSuggestion.created_at);
            setHasCompletedPolling(true);
            // Continue to set suggestions below - don't return early
          }
        } else {
          // No suggestions found during polling
          // Mark that polling has completed (even if no suggestions found)
          setHasCompletedPolling(true);
        }
      } else if (newSuggestions.length > 0) {
        // Store the latest timestamp when we first load suggestions
        const latestSuggestion = newSuggestions.reduce((latest, current) => {
          const currentTime = new Date(current.created_at || 0).getTime();
          const latestTime = new Date(latest.created_at || 0).getTime();
          return currentTime > latestTime ? current : latest;
        }, newSuggestions[0]);
        
        if (latestSuggestion.created_at) {
          setLastKnownSuggestionTimestamp(latestSuggestion.created_at);
        }
      } else {
        // No suggestions on initial load - set marker to indicate we've checked
        if (lastKnownSuggestionTimestamp === null) {
          setLastKnownSuggestionTimestamp("CHECKED_EMPTY");
        }
      }
      
      // Always set suggestions (don't return early) so they display in UI
      setSuggestions(newSuggestions);
    } catch (err) {
      console.error("Error fetching stored suggestions:", err);
      setError(err?.message || "Unable to fetch suggestions");
    } finally {
      setLoading(false);
    }
  };

  // Fetch on component mount and when visitId changes
  useEffect(() => {
    if (aiSuggestionsEnabled && visitId) {
      fetchStoredSuggestions();
      setHasCompletedPolling(false); // Reset when visit changes
    }
  }, [visitId, aiSuggestionsEnabled]);

  // Refresh suggestions when aiSuggestionsRefreshTrigger changes
  useEffect(() => {
    if (aiSuggestionsEnabled && aiSuggestionsRefreshTrigger && aiSuggestionsRefreshTrigger > 0) {
      fetchStoredSuggestions();
    }
  }, [aiSuggestionsRefreshTrigger, aiSuggestionsEnabled]);

  // Poll for new suggestions when EMR is saved
  useEffect(() => {
    if (!aiSuggestionsEnabled || !visitId || !isPollingForNewSuggestions) return;

    // Poll every 2 seconds
    // If we started with no suggestions (lastKnownSuggestionTimestamp is null or "CHECKED_EMPTY"), 
    // stop after 10 seconds (5 attempts) if still no suggestions
    // Otherwise, poll for up to 30 seconds (15 attempts) for new suggestions
    let pollCount = 0;
    const startedWithNoSuggestions = !lastKnownSuggestionTimestamp || lastKnownSuggestionTimestamp === "CHECKED_EMPTY";
    const maxPolls = startedWithNoSuggestions ? 5 : 15; // 10 seconds if no initial suggestions, 30 seconds otherwise
    let pollInterval = null;
    
    const poll = async () => {
      // Check if polling should stop before making API call
      if (!isPollingForNewSuggestions) {
        if (pollInterval) {
          clearInterval(pollInterval);
          pollInterval = null;
        }
        return;
      }
      
      pollCount++;
      
      try {
        await fetchStoredSuggestions(true); // Check for new suggestions
        
        // After fetchStoredSuggestions, check if it detected new suggestions
        // (it will set isPollingForNewSuggestions to false if new suggestions found)
        // Also stop if we reached max attempts
        if (pollCount >= maxPolls) {
          if (pollInterval) {
            clearInterval(pollInterval);
            pollInterval = null;
          }
          setIsPollingForNewSuggestions(false);
          setHasCompletedPolling(true);
          return;
        }
        
        // Check again if polling was stopped by fetchStoredSuggestions
        // (this happens when new suggestions are detected)
        if (!isPollingForNewSuggestions) {
          if (pollInterval) {
            clearInterval(pollInterval);
            pollInterval = null;
          }
          return;
        }
      } catch (err) {
        console.error("Error polling for suggestions:", err);
        // Stop polling on error
        if (pollInterval) {
          clearInterval(pollInterval);
          pollInterval = null;
        }
        setIsPollingForNewSuggestions(false);
      }
    };
    
    // Start polling immediately, then every 2 seconds
    poll();
    pollInterval = setInterval(poll, 2000);

    // Cleanup on unmount or when dependencies change
    return () => {
      if (pollInterval) {
        clearInterval(pollInterval);
      }
    };
  }, [visitId, isPollingForNewSuggestions, lastKnownSuggestionTimestamp, aiSuggestionsEnabled]); // Re-run when polling starts

  // Listen for EMR save events
  useEffect(() => {
    if (aiSuggestionsEnabled && onEmrSave) {
      // When EMR is saved, start polling for new suggestions
      const handleEmrSave = () => {
        setIsPollingForNewSuggestions(true);
        setNewSuggestionsAvailable(false);
        setHasCompletedPolling(false); // Reset polling completion flag
      };
      
      // Store the handler so we can clean it up
      window.addEventListener('emr-saved', handleEmrSave);
      
      return () => {
        window.removeEventListener('emr-saved', handleEmrSave);
      };
    }
  }, [onEmrSave, aiSuggestionsEnabled]);

  // Handle toggle to show/hide suggestions - fetch fresh data when opening
  const handleToggleSuggestions = async () => {
    if (!aiSuggestionsEnabled) return; // Don't do anything if AI is disabled
    
    if (!showSuggestions) {
      // When opening, fetch fresh data from API
      try {
        setLoading(true);
        const data = await AiDiagnosisService.getSuggestions(visitId);
        setSuggestions(data?.suggestions || []);
      } catch (err) {
        console.error("Error fetching suggestions:", err);
        setError(err?.message || "Unable to fetch suggestions");
      } finally {
        setLoading(false);
      }
    }
    setShowSuggestions((prev) => !prev);
  };

  // Handle accepting a suggestion
  const handleAcceptSuggestion = async (suggestionId) => {
    // Find the suggestion to check confidence
    const suggestion = suggestions.find(s => s.id === suggestionId);
    const confidence = suggestion?.confidence ?? 1.0;
    
    // If confidence is below 0.80, show confirmation dialog
    if (confidence < 0.80) {
      setPendingSuggestion(suggestionId);
      setShowConfirmationAlert(true);
      return;
    }
    
    // If confidence is 0.80 or above, proceed directly
    await proceedWithAcceptance(suggestionId);
  };

  // Proceed with accepting the suggestion
  const proceedWithAcceptance = async (suggestionId) => {
    try {
      await AiDiagnosisService.acceptSuggestion(suggestionId, organizationId, visitId);
      
      // Refresh suggestions to get updated status (including is_in_assessment)
      const data = await AiDiagnosisService.getSuggestions(visitId);
      setSuggestions(data?.suggestions || []);
      
      // Trigger assessment form refresh if callback is provided
      if (onSuggestionAccepted) {
        onSuggestionAccepted();
      }
      
      if (Toast && isSet(Toast.success, null)) {
        Toast.success("Suggestion accepted and added to assessment successfully");
      }
    } catch (err) {
      console.error("Error accepting suggestion:", err);
      if (Toast && isSet(Toast.error, null)) {
        Toast.error(err?.message || "Unable to accept suggestion");
      }
    }
  };

  // Handle confirmation dialog close
  const handleConfirmationClose = () => {
    setShowConfirmationAlert(false);
    setPendingSuggestion(null);
  };

  // Handle confirmation dialog confirm
  const handleConfirmationConfirm = async () => {
    if (pendingSuggestion) {
      setShowConfirmationAlert(false);
      await proceedWithAcceptance(pendingSuggestion);
      setPendingSuggestion(null);
    }
  };

  // If AI suggestions are disabled, show static view
  if (!aiSuggestionsEnabled) {
    return <AIPredictiveCharting />;
  }

  return (
    <div className="sh_cardBox sh_cardbggradient h-auto position-relative">
      <div className="sh_cardHeader pb-0">
        <div className="d-flex align-items-center ">
          <span className="pr-1">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M12 16.0019C12 13.7918 13.7916 12.0002 16.0017 12.0002C13.7916 12.0002 12 10.2086 12 7.99854C12 10.2086 10.2084 12.0002 7.99835 12.0002C10.2084 12.0002 12 13.7918 12 16.0019Z"
                stroke="white"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              ></path>
              <path
                d="M21.0037 12.0001C21.0037 16.9727 16.9726 21.0038 12 21.0038C7.02733 21.0038 2.99622 16.9727 2.99622 12.0001C2.99622 7.02745 7.02733 2.99634 12 2.99634"
                stroke="white"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              ></path>
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M19.0029 7.99835C19.0029 6.34081 20.3466 4.9971 22.0041 4.9971C20.3466 4.9971 19.0029 3.65339 19.0029 1.99585C19.0029 3.65339 17.6592 4.9971 16.0016 4.9971C17.6592 4.9971 19.0029 6.34081 19.0029 7.99835Z"
                stroke="white"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              ></path>
            </svg>
          </span>
          <h6 className="text-md font-weight-bold mb-0">AI - ICD Suggestions</h6>
        </div>
      </div>
      <div className="sh_cardBody">
        {loading && (
          <div className="d-flex align-items-center justify-content-center py-3">
            <span
              style={{
                width: 32,
                height: 32,
                borderRadius: "50%",
                border: "4px solid rgba(126, 224, 255, 0.3)",
                borderTopColor: "#7EE0FF",
                animation: "aiSpin 0.8s linear infinite",
                display: "inline-block",
                marginRight: 12,
              }}
            />
            <div style={{ color: "#7EE0FF", fontSize: "16px", fontWeight: "500" }}>Thinking...</div>
            <style>{`
              @keyframes aiSpin {
                from { transform: rotate(0deg) }
                to { transform: rotate(360deg) }
              }
            `}</style>
          </div>
        )}
        {error && (
          <div style={{ color: "#ff6b6b", fontSize: "16px", fontWeight: "500", padding: "12px", backgroundColor: "rgba(255, 107, 107, 0.1)", borderRadius: "8px" }}>
            {error}
          </div>
        )}
        {/* Only show "No suggestions available" if:
            - Not loading
            - No error
            - Not polling for new suggestions
            - No suggestions
            - We've confirmed there are no suggestions (initial load completed)
            - Polling has NOT just completed (to avoid showing it immediately after polling stops with no results) */}
        {!loading && !error && !isPollingForNewSuggestions && !hasCompletedPolling && suggestions.length === 0 && 
         (lastKnownSuggestionTimestamp === "CHECKED_EMPTY" || lastKnownSuggestionTimestamp === null) && (
          <div style={{ 
            color: "#7EE0FF", 
            fontSize: "18px", 
            fontWeight: "500", 
            textAlign: "center",
            padding: "20px",
            backgroundColor: "rgba(126, 224, 255, 0.1)",
            borderRadius: "8px",
            border: "2px solid rgba(126, 224, 255, 0.3)"
          }}>
            No suggestions available.
          </div>
        )}

        {/* Show polling indicator */}
        {isPollingForNewSuggestions && (
          <div 
            className="d-flex align-items-center justify-content-center mb-3" 
            style={{ 
              color: "#7EE0FF", 
              fontSize: "18px",
              fontWeight: "600",
              padding: "16px",
              backgroundColor: "rgba(126, 224, 255, 0.15)",
              borderRadius: "8px",
              border: "2px solid rgba(126, 224, 255, 0.4)"
            }}
          >
            <span
              style={{
                width: 28,
                height: 28,
                borderRadius: "50%",
                border: "4px solid rgba(126, 224, 255, 0.3)",
                borderTopColor: "#7EE0FF",
                animation: "aiSpin 0.8s linear infinite",
                display: "inline-block",
                marginRight: 12,
              }}
            />
            <span>Generating new suggestions...</span>
          </div>
        )}

        {/* Toggle to show/hide suggestions when available - only show if we have suggestions */}
        {!loading && !error && suggestions.length > 0 && (
          <>
            <button
              type="button"
              className="btn btn-sm mb-2"
              onClick={() => {
                handleToggleSuggestions();
                if (newSuggestionsAvailable) {
                  setNewSuggestionsAvailable(false);
                }
              }}
              style={{
                animation: (showSuggestions ? false : (newSuggestionsAvailable || isPollingForNewSuggestions))
                  ? "aiBlink 1.4s ease-in-out infinite"
                  : "none",
                backgroundColor: newSuggestionsAvailable ? "#7EE0FF" : "#f8f9fa",
                color: newSuggestionsAvailable ? "#1a1a2e" : "#212529",
                border: newSuggestionsAvailable ? "2px solid #5BC0DE" : "1px solid #dee2e6",
                fontWeight: newSuggestionsAvailable ? "600" : "400",
                fontSize: newSuggestionsAvailable ? "15px" : "14px",
                padding: newSuggestionsAvailable ? "10px 16px" : "6px 12px",
                boxShadow: newSuggestionsAvailable ? "0 2px 8px rgba(126, 224, 255, 0.4)" : "none",
              }}
            >
              {showSuggestions
                ? "Hide suggestions"
                : newSuggestionsAvailable
                ? `✨ View New Suggestions (${suggestions.length})`
                : `Suggestions available (${suggestions.length})`}
            </button>

            <style>{`
              @keyframes aiBlink {
                0% { box-shadow: 0 0 0px rgba(126, 224, 255, 0.0); }
                50% { box-shadow: 0 0 12px rgba(126, 224, 255, 0.65); }
                100% { box-shadow: 0 0 0px rgba(126, 224, 255, 0.0); }
              }
            `}</style>

            {showSuggestions && (
              <div 
                style={{
                  maxHeight: '400px',
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  paddingRight: '8px',
                }}
                className="ai-suggestions-scrollable"
              >
                <style>{`
                  .ai-suggestions-scrollable::-webkit-scrollbar {
                    width: 8px;
                  }
                  .ai-suggestions-scrollable::-webkit-scrollbar-track {
                    background: rgba(126, 224, 255, 0.1);
                    border-radius: 4px;
                  }
                  .ai-suggestions-scrollable::-webkit-scrollbar-thumb {
                    background: rgba(126, 224, 255, 0.5);
                    border-radius: 4px;
                  }
                  .ai-suggestions-scrollable::-webkit-scrollbar-thumb:hover {
                    background: rgba(126, 224, 255, 0.7);
                  }
                `}</style>
                <ul className="list-unstyled mb-0">
                  {suggestions.map((item, idx) => {
                  const isInAssessment = item.is_in_assessment || item.is_accepted;
                  return (
                    <li 
                      key={`${item.id || item.code}-${idx}`} 
                      className="mb-2 text-white"
                      style={{
                        opacity: isInAssessment ? 0.6 : 1,
                        textDecoration: isInAssessment ? 'line-through' : 'none',
                      }}
                    >
                      <div className="d-flex align-items-start justify-content-between">
                        <div className="flex-grow-1">
                          <div className="font-weight-bold d-flex align-items-center">
                            {item.code}
                            {isInAssessment && (
                              <span className="ml-2 text-success" title="Already in assessment">
                                ✓
                              </span>
                            )}
                          </div>
                          <div className="small">{item.description}</div>
                          <div className="small text-light">
                            Confidence: {item.confidence ?? "N/A"}
                          </div>
                          {item.is_accepted && (
                            <div className="small text-success mt-1">
                              ✓ Accepted
                            </div>
                          )}
                          {isInAssessment && !item.is_accepted && (
                            <div className="small text-info mt-1">
                              Already in assessment
                            </div>
                          )}
                        </div>
                        {!isInAssessment && (
                          <button
                            type="button"
                            className="btn btn-sm btn-success ml-2"
                            onClick={() => handleAcceptSuggestion(item.id)}
                            title="Accept this suggestion and add to assessment"
                          >
                            ✓
                          </button>
                        )}
                      </div>
                    </li>
                  );
                })}
                </ul>
              </div>
            )}
          </>
        )}
      </div>
      
      {/* Confirmation Alert for low confidence suggestions */}
      {showConfirmationAlert && pendingSuggestion && (() => {
        const suggestion = suggestions.find(s => s.id === pendingSuggestion);
        const confidence = suggestion?.confidence ?? 0;
        const confidencePercent = (confidence * 100).toFixed(0);
        
        return (
          <Alert
            type="warning"
            contentTitle="Low Confidence Warning"
            content={`This suggestion has a confidence level of ${confidencePercent}% (${confidence.toFixed(2)}). Do you want to accept this suggestion with this confidence level?`}
            onClose={handleConfirmationClose}
            onDelete={handleConfirmationConfirm}
            yesButtonName="Yes, Accept"
            noButtonName="Cancel"
          />
        );
      })()}
    </div>
  );
};

export default withReduxState(AIPrediction);